import { List, ListItem, mq, Text, TextProps } from "@certn/cascadia-react";
import { layouts } from "@certn/cascadia-tokens";
import { css } from "@emotion/css";
import ReactMarkdown from "react-markdown";

import { Code } from "./code/Code";

interface Props {
  children: string;
  testId: string;
}

const headingToId = (heading: string) => {
  return heading.toLowerCase().replace(/ /g, "-");
};

const Heading = ({
  children,
  as,
  style,
}: Pick<TextProps, "as" | "children" | "style">) => {
  const id = headingToId(String(children));
  return (
    <Text as={as} id={id} testID={id} style={style}>
      {children}
    </Text>
  );
};

export function Markdown({ children: markdown, testId }: Props) {
  return (
    <ReactMarkdown
      data-testid={testId}
      className={styles.markdown}
      components={{
        p: ({ children }) => (
          <Text className={styles.text} as="p" style="global/body-secondary">
            {children}
          </Text>
        ),
        h1: ({ children }) => (
          <Heading as="h1" style="global/title-small">
            {children}
          </Heading>
        ),
        h2: ({ children }) => (
          <Heading as="h2" style="global/heading-large">
            {children}
          </Heading>
        ),
        h3: ({ children }) => (
          <Heading as="h3" style="global/heading-medium">
            {children}
          </Heading>
        ),
        h4: ({ children }) => (
          <Heading as="h4" style="global/heading-small">
            {children}
          </Heading>
        ),
        h5: ({ children }) => (
          <Heading as="h5" style="global/body-secondary">
            {children}
          </Heading>
        ),
        h6: ({ children }) => (
          <Heading as="h6" style="global/body-secondary">
            {children}
          </Heading>
        ),
        ul: ({ children }) => (
          <div className={styles.list}>
            <List type="unordered">{children}</List>
          </div>
        ),
        ol: ({ children }) => (
          <div className={styles.list}>
            <List type="ordered">{children}</List>
          </div>
        ),
        li: ({ children }) => <ListItem size="secondary">{children}</ListItem>,
        code: (props) => {
          const { children, className } = props;
          const language = /language-(\w+)/.exec(className || "");
          return (
            <Code
              testId="markdown-code-snippet"
              language={language ? language[1] : "bash"}
            >
              {children}
            </Code>
          );
        },
      }}
    >
      {markdown}
    </ReactMarkdown>
  );
}

const styles = {
  markdown: css`
    font-family: "var(--font-family-default)";

    & > :last-child {
      margin-bottom: 0;
    }
  `,
  text: css(
    {
      marginBottom: "var(--space-3)",
    },
    mq({ maxWidth: layouts.narrow.maxWidth })
  ),
  list: css({
    marginBottom: "var(--space-3)",
  }),
};
